/**
 * Read-only post grid display. Supports text, link, date, message, file (image/video/audio),
 * and map cell types. Also accepts cardBackground for card-level backgrounds.
 */
import React, { useState, useCallback } from 'react';
import { View, Text, Image, ImageBackground, StyleSheet, TouchableOpacity, Linking, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { buildGridFromItems, GRID_COLS, GRID_ROWS, resolveCellBorderRadiusPx } from '../utils/postGridEngine';
import { getResponsiveValue } from '../styles/breakpoints';
import { MAPBOX_ACCESS_TOKEN } from '../config/mapbox-config';
import { CompactAudioPlayer } from './CompactAudioPlayer';
import {
  getLinkDisplayMode,
  getLinkButtonLabel,
  getLinkButtonBg,
  getLinkButtonFg,
  getLinkButtonShape,
  getLinkButtonSize,
  getLinkButtonMetrics,
  linkButtonShapeToBorderRadius,
  LINK_DISPLAY_BUTTON,
} from '../utils/postGridLink';
import { FullscreenMapModal } from './FullscreenMapModal';

export const COL_WIDTH = getResponsiveValue({ mobile: 110, tablet: 110, laptop: 120, desktop: 120 });
export const ROW_HEIGHT = getResponsiveValue({ mobile: 110, tablet: 110, laptop: 120, desktop: 120 });

const FONT_SIZE_MAP = { sm: 13, md: 18, lg: 24, xl: 32 };

const DEFAULT_POST_GRID_FONT = 'Computer Says No';

/**
 * Maps stored widget `fontFamily` to a value RN / RN-web will actually apply.
 * On web, multi-word families need quoting in CSS; Arial benefits from a small stack.
 */
export function resolvePostGridFontFamily(fontFamily) {
  if (fontFamily == null || String(fontFamily).trim() === '') {
    return DEFAULT_POST_GRID_FONT;
  }
  const name = String(fontFamily).trim();
  if (Platform.OS === 'web') {
    if (name === 'Arial') return 'Arial, Helvetica Neue, Helvetica, sans-serif';
    if (name.includes(' ')) return `"${name}", sans-serif`;
    return name;
  }
  return name;
}

function cellBorderRadiusStyle(item, cellWidthPx, cellHeightPx) {
  const r = resolveCellBorderRadiusPx(item, cellWidthPx, cellHeightPx);
  if (r == null || r <= 0) return undefined;
  return { borderRadius: r };
}

const parseCalendarValue = (value) => {
  if (!value || typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (_) {
    return { date: value };
  }
  return null;
};

const prettyDate = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatCalendarText = (rawValue) => {
  const parsed = parseCalendarValue(rawValue);
  const start = parsed?.startDate || parsed?.date || '';
  const end = parsed?.endDate || '';
  if (!start) return '';
  if (!end) return prettyDate(start);
  return `${prettyDate(start)} - ${prettyDate(end)}`;
};

function openLink(url) {
  if (!url) return;
  const href = url.startsWith('http') ? url : `https://${url}`;
  if (Platform.OS === 'web') {
    window.open(href, '_blank', 'noopener,noreferrer');
  } else {
    Linking.openURL(href).catch(() => {});
  }
}

function VideoCell({ url, name, cellStyle }) {
  if (Platform.OS === 'web') {
    return (
      <View style={[cellStyle, { overflow: 'hidden' }]}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          src={url}
          playsInline
          controls
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </View>
    );
  }
  return (
    <TouchableOpacity style={[cellStyle, styles.mediaCellDark]} onPress={() => Linking.openURL(url).catch(() => {})}>
      <Ionicons name="videocam" size={22} color="rgba(255,255,255,0.9)" />
      <Text style={styles.mediaLabel} numberOfLines={1}>{name || 'Video'}</Text>
    </TouchableOpacity>
  );
}

function AudioCell({ url, name, compact = false }) {
  return <CompactAudioPlayer url={url} name={name} compact={compact} accentColor="#1075ae" />;
}

function MapCell({ value, colSpan, rowSpan, colWidth, rowHeight, onPress }) {
  let parsed = null;
  try { parsed = JSON.parse(value); } catch (_) {}
  if (!parsed || parsed.lat == null || parsed.lng == null) {
    return (
      <View style={styles.mapPlaceholder}>
        <Ionicons name="map-outline" size={20} color="#999" />
        <Text style={styles.cellPlaceholder}>Map</Text>
      </View>
    );
  }
  const w = Math.round((colSpan || 1) * (colWidth || COL_WIDTH));
  const h = Math.round((rowSpan || 1) * (rowHeight || ROW_HEIGHT));
  const mapUrl = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-s+e74c3c(${parsed.lng},${parsed.lat})/${parsed.lng},${parsed.lat},${parsed.zoom || 13}/${w}x${h}@2x?access_token=${MAPBOX_ACCESS_TOKEN}`;
  const handlePress = () => {
    if (onPress) {
      onPress(parsed);
    } else {
      openLink(`https://www.google.com/maps?q=${parsed.lat},${parsed.lng}`);
    }
  };
  return (
      <TouchableOpacity
        style={{ width: w, height: h }}
        onPress={handlePress}
        activeOpacity={0.85}
      >
      <Image source={{ uri: mapUrl }} style={{ width: w, height: h }} resizeMode="cover" />
      {parsed.label ? (
        <View style={styles.mapLabelWrap}>
          <Text style={styles.mapLabelText} numberOfLines={1}>{parsed.label}</Text>
        </View>
      ) : null}
    </TouchableOpacity>
  );
}

function renderCellContent(
  widget,
  colSpan,
  rowSpan,
  colWidth,
  rowHeight,
  compactMode = false,
  onMapPress,
  suppressEmptyWidgetHints = false,
  onImagePress,
  fontScale = 1
) {
  if (!widget) return null;
  const { type, value, fontSize, cellBg, textColor, fontFamily } = widget;
  if (type === 'empty') return null;
  const scaleFont = (size) => Math.max(1, Math.round(Number(size) * fontScale));
  const baseFontSize = scaleFont(FONT_SIZE_MAP[fontSize] || getResponsiveValue({ mobile: 18, tablet: 20, laptop: 24, desktop: 25 }));
  const colorStyle = textColor ? { color: textColor } : undefined;
  const textAlign = ['left', 'center', 'right'].includes(widget?.textAlign) ? widget.textAlign : 'center';
  const fontResolved = { fontFamily: resolvePostGridFontFamily(fontFamily) };

  if (type === 'text') {
    return (
      <Text style={[styles.cellText, { fontSize: compactMode ? scaleFont(16) : baseFontSize, textAlign }, colorStyle, fontResolved]}>
        {value || ''}
      </Text>
    );
  }

  if (type === 'link') {
    if (!value && suppressEmptyWidgetHints) return null;
    const display = getLinkDisplayMode(widget);
    if (display === LINK_DISPLAY_BUTTON) {
      const label = getLinkButtonLabel(widget);
      const bg = getLinkButtonBg(widget);
      const fg = getLinkButtonFg(widget);
      const radius = linkButtonShapeToBorderRadius(getLinkButtonShape(widget));
      const m = getLinkButtonMetrics(getLinkButtonSize(widget), !!compactMode);
      return (
        <TouchableOpacity
          onPress={() => openLink(value)}
          style={styles.linkWrap}
          activeOpacity={0.85}
        >
          <View
            style={[
              styles.linkButton,
              {
                backgroundColor: bg,
                borderRadius: radius,
                paddingHorizontal: m.padH,
                paddingVertical: m.padV,
              },
            ]}
          >
            <Text
              style={[
                styles.linkButtonText,
                { color: fg, fontSize: scaleFont(m.fontSize) },
                fontResolved,
              ]}
              numberOfLines={2}
            >
              {label}
            </Text>
          </View>
        </TouchableOpacity>
      );
    }
    return (
      <TouchableOpacity onPress={() => openLink(value)} style={styles.linkWrap} activeOpacity={0.85}>
        <Text
          style={[styles.cellText, styles.cellLink, { fontSize: compactMode ? scaleFont(14) : baseFontSize }, colorStyle, fontResolved]}
          numberOfLines={3}
        >
          {value || 'https://...'}
        </Text>
      </TouchableOpacity>
    );
  }

  if (type === 'date' || type === 'calendar') {
    const dateText = formatCalendarText(value);
    if (!dateText && suppressEmptyWidgetHints) return null;
    const hasDate = !!dateText;
    const calendarColor = textColor || (hasDate ? '#2e5aac' : '#999');
    const calFontSize = scaleFont(compactMode ? 11 : (FONT_SIZE_MAP[fontSize] || 16));
    return (
      <View style={styles.calendarValueWrap}>
        <Ionicons name="calendar-outline" size={compactMode ? 16 : 26} color={calendarColor} />
        <Text
          style={[
            styles.calendarValueText,
            hasDate && styles.calendarFilledText,
            { color: calendarColor, fontSize: calFontSize },
            fontResolved,
          ]}
          numberOfLines={1}
        >
          {dateText || 'Set calendar date'}
        </Text>
      </View>
    );
  }

  if (type === 'message') {
    if (suppressEmptyWidgetHints) return null;
    return (
      <View style={styles.messageCellInner}>
        <Ionicons name="chatbubble-outline" size={compactMode ? 10 : 14} color="#999" />
        <Text style={styles.cellPlaceholder}>Message</Text>
      </View>
    );
  }

  if (type === 'map') {
    if (suppressEmptyWidgetHints) {
      let parsedMap = null;
      try {
        parsedMap = value ? JSON.parse(value) : null;
      } catch (_) {}
      if (!parsedMap || parsedMap.lat == null || parsedMap.lng == null) return null;
    }
    return (
      <MapCell
        value={value}
        colSpan={colSpan}
        rowSpan={rowSpan}
        colWidth={colWidth}
        rowHeight={rowHeight}
        onPress={onMapPress}
      />
    );
  }

  if (type === 'audio') {
    if (!value && suppressEmptyWidgetHints) return null;
    if (value) {
      try {
        const parsed = JSON.parse(value);
        if (parsed?.url) return <AudioCell url={parsed.url} name={parsed.name} compact={compactMode} />;
      } catch (_) {}
    }
    if (suppressEmptyWidgetHints) return null;
    return (
      <View style={styles.messageCellInner}>
        <Ionicons name="musical-notes-outline" size={14} color="#999" />
        <Text style={styles.cellPlaceholder}>Audio</Text>
      </View>
    );
  }

  if (type === 'file' && value) {
    try {
      const parsed = JSON.parse(value);
      if (parsed?.url) {
        const isImage = (parsed.mimeType && parsed.mimeType.startsWith('image/')) ||
          /\.(jpe?g|png|gif|webp|bmp)$/i.test(parsed.name || '');
        const isVideo = (parsed.mimeType && parsed.mimeType.startsWith('video/')) ||
          /\.(mp4|webm|mov|avi|m4v)$/i.test(parsed.name || '');
        const isAudio = !isVideo && ((parsed.mimeType && parsed.mimeType.startsWith('audio/')) ||
          /\.(mp3|wav|wave|ogg|aac|flac|m4a)$/i.test(parsed.name || ''));
        const isPdf = (parsed.mimeType === 'application/pdf') || /\.pdf$/i.test(parsed.name || '');
        if (isImage) {
          const imgEl = <Image source={{ uri: parsed.url }} style={styles.cellMedia} resizeMode="cover" />;
          if (onImagePress) {
            return (
              <TouchableOpacity onPress={(e) => { if (e?.stopPropagation) e.stopPropagation(); onImagePress(parsed.url); }} style={{ flex: 1, width: '100%', height: '100%' }} activeOpacity={0.85}>
                {imgEl}
              </TouchableOpacity>
            );
          }
          return imgEl;
        }
        if (isVideo) {
          return <VideoCell url={parsed.url} name={parsed.name} cellStyle={styles.cellMedia} />;
        }
        if (isAudio) {
          return <AudioCell url={parsed.url} name={parsed.name} compact={compactMode} />;
        }
        if (isPdf) {
          return (
            <TouchableOpacity
              style={[styles.cellMedia, styles.mediaCellDark]}
              onPress={() => openLink(parsed.url)}
              activeOpacity={0.85}
            >
              <Ionicons name="document-text" size={22} color="rgba(255,255,255,0.9)" />
              <Text style={styles.mediaLabel} numberOfLines={1}>{parsed.name || 'PDF'}</Text>
            </TouchableOpacity>
          );
        }
      }
    } catch (_) {}
    if (suppressEmptyWidgetHints) return null;
    return (
      <View style={styles.placeholderWrap}>
        <Ionicons name="image-outline" size={14} color="#999" />
        <Text style={styles.cellPlaceholder} numberOfLines={1}>File</Text>
      </View>
    );
  }

  if (type === 'file') {
    if (suppressEmptyWidgetHints) return null;
    return (
      <View style={styles.placeholderWrap}>
        <Ionicons name="image-outline" size={14} color="#999" />
        <Text style={styles.cellPlaceholder}>Image / Video / Audio</Text>
      </View>
    );
  }

  return (
    <Text style={[styles.cellText, { fontSize: compactMode ? scaleFont(16) : baseFontSize }, colorStyle, fontResolved]} numberOfLines={2}>
      {value || ''}
    </Text>
  );
}

// Renders a video element as an absolute background (web only)
function VideoBackground({ url }) {
  if (Platform.OS !== 'web') return null;
  return (
    <video
      src={url}
      autoPlay
      loop
      muted
      playsInline
      style={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        objectFit: 'cover', zIndex: 0,
      }}
    />
  );
}

export default function PostGridDisplay({
  items = [],
  colWidth = COL_WIDTH,
  rowHeight = ROW_HEIGHT,
  style,
  compactMode = false,
  cardBackground = null,
  onMapPress,
  suppressEmptyWidgetHints = false,
  onImagePress,
  fontScale = 1,
}) {
  const postItems = Array.isArray(items) ? items : [];
  const grid = buildGridFromItems(postItems);

  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [mapFullscreenCenter, setMapFullscreenCenter] = useState(null);
  const [imageFullscreen, setImageFullscreen] = useState(false);
  const [imageFullscreenUrl, setImageFullscreenUrl] = useState(null);

  const handleMapPressDefault = useCallback((parsed) => {
    if (!parsed || parsed.lat == null || parsed.lng == null) return;
    if (Platform.OS === 'web' && MAPBOX_ACCESS_TOKEN) {
      setMapFullscreenCenter([parsed.lng, parsed.lat]);
      setMapFullscreen(true);
    } else {
      openLink(`https://www.google.com/maps?q=${parsed.lat},${parsed.lng}`);
    }
  }, []);

  const effectiveOnMapPress = onMapPress || handleMapPressDefault;

  const handleImagePressDefault = useCallback((url) => {
    if (onImagePress) {
      onImagePress(url);
    } else {
      setImageFullscreenUrl(url);
      setImageFullscreen(true);
    }
  }, [onImagePress]);

  const gridContent = (
    <View style={[styles.gridOuter, { width: GRID_COLS * colWidth }, style]}>
      {/* Card-level video background (web only) */}
      {cardBackground?.type === 'video' && cardBackground.value ? (
        <VideoBackground url={cardBackground.value} />
      ) : null}

      {Array.from({ length: GRID_ROWS }, (_, rowIndex) => (
        <View key={rowIndex} style={[styles.gridRow, { height: rowHeight, zIndex: 10 - rowIndex }]}>
          {Array.from({ length: GRID_COLS }, (_, colIndex) => {
            const cell = grid[rowIndex][colIndex];
            if (cell?.type === 'covered') {
                const isSameRowAsStart = rowIndex === cell.item.y;
                if (isSameRowAsStart) return null;
                return (
                <View
                  key={`covered-${rowIndex}-${colIndex}`}
                  style={[styles.cellEmpty, styles.cellCovered, { width: colWidth, height: rowHeight }]}
                  pointerEvents="none"
                />
              );
            }
            if (!cell) {
              return (
                <View
                  key={`empty-${rowIndex}-${colIndex}`}
                  style={[styles.cellEmpty, { width: colWidth, height: rowHeight }]}
                />
              );
            }
            const { item } = cell;
            const colSpan = item.w || 1;
            const rowSpan = item.h || 1;
            const cellBgColor = item.cellBg || undefined;
            const isMerged = rowSpan > 1 || colSpan > 1;
            return (
              <View
                key={item.id}
                style={[
                  styles.cell,
                  { width: colSpan * colWidth, minHeight: rowSpan * rowHeight },
                  isMerged && rowSpan > 1 && { height: rowSpan * rowHeight, alignSelf: 'flex-start' },
                  cellBgColor ? { backgroundColor: cellBgColor } : undefined,
                  cellBorderRadiusStyle(item, colSpan * colWidth, rowSpan * rowHeight),
                ]}
              >
                <View style={styles.cellInner}>
                  {renderCellContent(item, colSpan, rowSpan, colWidth, rowHeight, compactMode, effectiveOnMapPress, suppressEmptyWidgetHints, handleImagePressDefault, fontScale)}
                </View>
              </View>
            );
          })}
        </View>
      ))}
    </View>
  );

  const content = (() => {
    if (cardBackground?.type === 'color' && cardBackground.value) {
      return (
        <View style={[styles.gridOuter, { width: GRID_COLS * colWidth, backgroundColor: cardBackground.value }, style]}>
        {Array.from({ length: GRID_ROWS }, (_, rowIndex) => (
          <View key={rowIndex} style={[styles.gridRow, { height: rowHeight }]}>
            {Array.from({ length: GRID_COLS }, (_, colIndex) => {
              const cell = grid[rowIndex][colIndex];
              if (cell?.type === 'covered') {
                const isSameRowAsStart = rowIndex === cell.item.y;
                if (isSameRowAsStart) return null;
                return <View key={`covered-${rowIndex}-${colIndex}`} style={[styles.cellEmpty, styles.cellCovered, { width: colWidth, height: rowHeight }]} pointerEvents="none" />;
              }
              if (!cell) return <View key={`empty-${rowIndex}-${colIndex}`} style={[styles.cellEmpty, { width: colWidth, height: rowHeight }]} />;
              const { item } = cell;
              const colSpan = item.w || 1;
              const rowSpan = item.h || 1;
              const isMerged = rowSpan > 1 || colSpan > 1;
              return (
                <View key={item.id} style={[styles.cell, { width: colSpan * colWidth, minHeight: rowSpan * rowHeight }, isMerged && rowSpan > 1 && { height: rowSpan * rowHeight, alignSelf: 'flex-start' }, item.cellBg ? { backgroundColor: item.cellBg } : undefined, cellBorderRadiusStyle(item, colSpan * colWidth, rowSpan * rowHeight)]}>
                  <View style={styles.cellInner}>{renderCellContent(item, colSpan, rowSpan, colWidth, rowHeight, compactMode, effectiveOnMapPress, suppressEmptyWidgetHints, handleImagePressDefault, fontScale)}</View>
                </View>
              );
            })}
          </View>
        ))}
        </View>
      );
    }

    if (cardBackground?.type === 'image' && cardBackground.value) {
      return (
        <ImageBackground
          source={{ uri: cardBackground.value }}
          style={[styles.gridOuter, { width: GRID_COLS * colWidth }, style]}
          resizeMode="cover"
        >
        {Array.from({ length: GRID_ROWS }, (_, rowIndex) => (
          <View key={rowIndex} style={[styles.gridRow, { height: rowHeight }]}>
            {Array.from({ length: GRID_COLS }, (_, colIndex) => {
              const cell = grid[rowIndex][colIndex];
              if (cell?.type === 'covered') {
                const isSameRowAsStart = rowIndex === cell.item.y;
                if (isSameRowAsStart) return null;
                return <View key={`covered-${rowIndex}-${colIndex}`} style={[styles.cellEmpty, styles.cellCovered, { width: colWidth, height: rowHeight }]} pointerEvents="none" />;
              }
              if (!cell) return <View key={`empty-${rowIndex}-${colIndex}`} style={[styles.cellEmpty, { width: colWidth, height: rowHeight }]} />;
              const { item } = cell;
              const colSpan = item.w || 1;
              const rowSpan = item.h || 1;
              const isMerged = rowSpan > 1 || colSpan > 1;
              return (
                <View key={item.id} style={[styles.cell, { width: colSpan * colWidth, minHeight: rowSpan * rowHeight }, isMerged && rowSpan > 1 && { height: rowSpan * rowHeight, alignSelf: 'flex-start' }, item.cellBg ? { backgroundColor: item.cellBg } : undefined, cellBorderRadiusStyle(item, colSpan * colWidth, rowSpan * rowHeight)]}>
                  <View style={styles.cellInner}>{renderCellContent(item, colSpan, rowSpan, colWidth, rowHeight, compactMode, effectiveOnMapPress, suppressEmptyWidgetHints, handleImagePressDefault, fontScale)}</View>
                </View>
              );
            })}
          </View>
        ))}
        </ImageBackground>
      );
    }

    return gridContent;
  })();

  return (
    <>
      {content}
      <FullscreenMapModal
        visible={mapFullscreen}
        onClose={() => setMapFullscreen(false)}
        center={mapFullscreenCenter}
      />
      {/* Fullscreen image viewer */}
      {imageFullscreen && imageFullscreenUrl && Platform.OS === 'web' && (
        <div
          onClick={() => setImageFullscreen(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <img
            src={imageFullscreenUrl}
            alt="Fullscreen"
            style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 8 }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setImageFullscreen(false)}
            style={{
              position: 'absolute', top: 20, right: 24,
              background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 20,
              color: '#fff', fontSize: 28, cursor: 'pointer', width: 40, height: 40,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >✕</button>
        </div>
      )}
    </>
  );
}

export { COL_WIDTH as POST_GRID_COL_WIDTH, ROW_HEIGHT as POST_GRID_ROW_HEIGHT };

const styles = StyleSheet.create({
  gridOuter: {
    backgroundColor: 'transparent',
    alignSelf: 'center',
    justifyContent: 'space-between',
    overflow: 'hidden',
    position: 'relative',
  },
  gridRow: {
    flexDirection: 'row',
    position: 'relative',
    zIndex: 1,
  },
  cellEmpty: {
    backgroundColor: 'transparent',
  },
  cellCovered: {
    backgroundColor: 'transparent',
  },
  cell: {
    backgroundColor: 'transparent',
    overflow: 'hidden',
  },
  cellInner: {
    flex: 1,
    padding: 4,
    minHeight: 24,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  cellText: {
    textAlign: 'center',
    color: '#000',
  },
  linkWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  linkButton: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    maxWidth: '100%',
  },
  linkButtonText: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    textAlign: 'center',
  },
  calendarValueWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },
  calendarValueText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: '#999',
    marginTop: 4,
    textAlign: 'center',
  },
  calendarFilledText: {
    fontWeight: '700',
  },
  cellLink: {
    color: '#2ecc71',
    textDecorationLine: 'underline',
  },
  messageCellInner: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  cellPlaceholder: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 8,
    color: '#999',
    marginTop: 2,
  },
  placeholderWrap: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  cellMedia: {
    width: '100%',
    height: '100%',
    minHeight: 28,
  },
  mediaCellDark: {
    flex: 1,
    backgroundColor: '#333',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 2,
  },
  mediaLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 8,
    color: 'rgba(255,255,255,0.9)',
    marginTop: 2,
  },
  audioWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
    width: '100%',
  },
  audioLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 9,
    color: '#555',
    marginTop: 2,
    textAlign: 'center',
  },
  mapPlaceholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mapLabelWrap: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    paddingVertical: 3,
    paddingHorizontal: 6,
  },
  mapLabelText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 10,
    color: '#fff',
    textAlign: 'center',
  },
});
